import React, { useMemo } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';

import { ICombo, IPicker } from '@rbi-ctg/menu';
import { BonusPoints } from 'components/bonus-points/bonus-points';
import { ReadMoreAccordion } from 'components/read-more-accordion';
import { SanityBlockRenderer } from 'components/sanity-block-renderer';
import { useFeatureMenu } from 'hooks/use-feature-menu';
import { MediaQuery, useMediaQuery } from 'hooks/use-media-query';
import { useProductWizardContext } from 'state/product-wizard';

import { NutritionInformation } from '../nutrition-information';
import { PriceAndCalories } from '../price-and-calories';
import { createPickerPromotionMap } from '../utils';

import {
  BackgroundImageColor,
  OverviewContainer,
  StyledProductPicture,
  Title,
} from './product-overview.styled';

export const ProductOverview: React.FC = () => {
  const { menuObject, selectedProduct, userSelections } = useProductWizardContext();
  const { featureMenu } = useFeatureMenu();

  const isDesktop = useMediaQuery(MediaQuery.Desktop);

  const promotionBonusPoints = useMemo(() => {
    if (!userSelections.pickerAspects) {
      return null;
    }
    const pickerPromotionMap = createPickerPromotionMap((menuObject as IPicker).options);
    let promotion;
    for (const pickerSelectionId in userSelections.pickerAspects) {
      const pickerIdentifier = userSelections.pickerAspects[pickerSelectionId];
      promotion = pickerPromotionMap[`${pickerSelectionId}-${pickerIdentifier}`];
      if (promotion) {
        break;
      }
    }
    return promotion?.bonusPoints;
  }, [menuObject, userSelections]);

  const productImage =
    featureMenu?.enableDynamicPickerImages && selectedProduct?.image
      ? selectedProduct?.image
      : menuObject?.image;

  const boxMenuObject = menuObject as ICombo;

  const description = menuObject.description
    ? menuObject.description.localeRaw
    : boxMenuObject?.mainItem?.description?.localeRaw;

  return (
    <OverviewContainer>
      <Box flexDirection="column" position="relative">
        <BackgroundImageColor />
        <StyledProductPicture image={productImage} alt="" />
      </Box>
      <Box
        padding="0 1rem 1rem"
        flexDirection="column"
        width={isDesktop ? '464px' : '100%'}
        margin="0 auto"
      >
        <Box alignContent="center" align="center" alignSelf="center">
          <Title data-testid="product-title">{selectedProduct?.name?.locale ?? ''}</Title>
        </Box>
        <PriceAndCalories />
        <ReadMoreAccordion>
          <SanityBlockRenderer content={description} />
        </ReadMoreAccordion>
        {promotionBonusPoints && <BonusPoints bonusPoints={promotionBonusPoints} />}
        {isDesktop && <NutritionInformation />}
      </Box>
    </OverviewContainer>
  );
};
